/**
 * Background Manager
 * 
 * Manages scene backgrounds including solid colors, gradients, HDRI,
 * and custom images.
 */

import * as THREE from 'three';
import type { BackgroundConfig, BackgroundType, IManager } from '../types';

export interface BackgroundManagerConfig {
  background?: BackgroundConfig;
  // Legacy support
  backgroundColor?: string;
  backgroundUrl?: string;
}

export class BackgroundManager implements IManager {
  private scene: THREE.Scene | null = null;
  // Stored for potential future use (HDRI processing, etc.)
  public renderer: THREE.WebGLRenderer | null = null;
  private currentType: BackgroundType = 'color';
  
  // Gradient resources
  private gradientMesh: THREE.Mesh | null = null;
  
  // Texture resources
  private backgroundTexture: THREE.Texture | null = null;
  
  /**
   * Initialize background
   */
  public async init(scene: THREE.Scene, config: BackgroundManagerConfig): Promise<void> {
    this.scene = scene;
    
    // Handle legacy config
    if (!config.background) {
      if (config.backgroundColor) {
        this.setColor(config.backgroundColor);
      }
      return;
    }
    
    const bgConfig = config.background;
    
    switch (bgConfig.type) {
      case 'color':
        this.setColor(bgConfig.color ?? '#000000');
        break;
        
      case 'gradient':
        this.setGradient(
          bgConfig.gradientColors ?? ['#1a1a2e', '#16213e'],
          bgConfig.gradientAngle ?? 180
        );
        break;
        
      case 'image':
        if (bgConfig.imageUrl) {
          await this.setImage(bgConfig.imageUrl);
        }
        break;
        
      case 'hdri':
        // HDRI is handled separately by HDRILoader
        // Just mark that we're using HDRI
        this.currentType = 'hdri';
        break;
    }
  }
  
  /**
   * Set renderer for gradient rendering
   */
  public setRenderer(renderer: THREE.WebGLRenderer): void {
    this.renderer = renderer;
  }
  
  /**
   * Set solid color background
   */
  public setColor(color: string): void {
    this.cleanup();
    this.currentType = 'color';
    
    if (this.scene) {
      this.scene.background = new THREE.Color(color);
    }
  }
  
  /**
   * Set gradient background
   */
  public setGradient(colors: string[], angle = 180): void {
    this.cleanup();
    this.currentType = 'gradient';
    
    if (!this.scene) return;
    
    // Create gradient texture
    const canvas = document.createElement('canvas');
    canvas.width = 2;
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;
    
    // Create gradient
    const angleRad = (angle - 90) * (Math.PI / 180);
    const x1 = 0.5 + Math.cos(angleRad) * 0.5;
    const y1 = 0.5 + Math.sin(angleRad) * 0.5;
    const x2 = 0.5 - Math.cos(angleRad) * 0.5;
    const y2 = 0.5 - Math.sin(angleRad) * 0.5;
    
    const gradient = ctx.createLinearGradient(
      x1 * canvas.width,
      y1 * canvas.height,
      x2 * canvas.width,
      y2 * canvas.height
    );
    
    // Add color stops
    const step = 1 / (colors.length - 1);
    colors.forEach((color, i) => {
      gradient.addColorStop(i * step, color);
    });
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    
    this.backgroundTexture = texture;
    this.scene.background = texture;
  }
  
  /**
   * Set image background
   */
  public async setImage(url: string): Promise<void> {
    this.cleanup();
    this.currentType = 'image';
    
    if (!this.scene) return;
    
    return new Promise((resolve, reject) => {
      const loader = new THREE.TextureLoader();
      
      loader.load(
        url,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          this.backgroundTexture = texture;
          this.scene!.background = texture;
          resolve();
        },
        undefined,
        (error) => {
          console.error('Failed to load background image:', error);
          reject(error);
        }
      );
    });
  }
  
  /**
   * Set HDRI background (texture provided externally)
   */
  public setHdriBackground(texture: THREE.Texture): void {
    this.cleanup();
    this.currentType = 'hdri';
    
    if (this.scene) {
      this.backgroundTexture = texture;
      this.scene.background = texture;
    }
  }
  
  /**
   * Clear background (transparent)
   */
  public clear(): void {
    this.cleanup();
    
    if (this.scene) {
      this.scene.background = null;
    }
  }
  
  /**
   * Get current background type
   */
  public getType(): BackgroundType {
    return this.currentType;
  }
  
  /**
   * Create animated gradient (for advanced effects)
   */
  public createAnimatedGradient(
    colors: string[],
    speed = 1
  ): (time: number) => void {
    // Create shader material for animated gradient
    const material = new THREE.ShaderMaterial({
      uniforms: {
        color1: { value: new THREE.Color(colors[0] ?? '#000000') },
        color2: { value: new THREE.Color(colors[1] ?? '#ffffff') },
        color3: { value: new THREE.Color(colors[2] ?? colors[1] ?? '#ffffff') },
        time: { value: 0 },
        speed: { value: speed },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 color1;
        uniform vec3 color2;
        uniform vec3 color3;
        uniform float time;
        uniform float speed;
        varying vec2 vUv;
        
        void main() {
          float t = vUv.y + sin(time * speed) * 0.1;
          vec3 color;
          if (t < 0.5) {
            color = mix(color1, color2, t * 2.0);
          } else {
            color = mix(color2, color3, (t - 0.5) * 2.0);
          }
          gl_FragColor = vec4(color, 1.0);
        }
      `,
      depthWrite: false,
    });
    
    // Return update function
    return (time: number) => {
      material.uniforms.time.value = time;
    };
  }
  
  /**
   * Export current background config
   */
  public getConfig(): BackgroundConfig {
    return {
      type: this.currentType,
      // Add other properties based on current state
    };
  }
  
  /**
   * Cleanup resources
   */
  private cleanup(): void {
    if (this.backgroundTexture) {
      this.backgroundTexture.dispose();
      this.backgroundTexture = null;
    }
    
    if (this.gradientMesh) {
      this.gradientMesh.geometry.dispose();
      (this.gradientMesh.material as THREE.Material).dispose();
      this.gradientMesh = null;
    }
  }
  
  /**
   * Update (called every frame)
   */
  public update(_deltaTime: number): void {
    // For animated backgrounds if needed
  }
  
  /**
   * Dispose all resources
   */
  public dispose(): void {
    this.cleanup();
    this.scene = null;
    this.renderer = null;
  }
}
